import clsx from "clsx";
import { useFormik } from "formik";
import moment from "moment/moment";
import React, { useEffect, useState } from "react";
import { RxCross2 } from "react-icons/rx";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import DataTableWrapper from "../../components/DataTableWrapper";
import DatePicker from "../../components/DatePicker";
import DotBall from "../../components/DotBall";
import Dropdown from "../../components/Dropdown";
import Loader from "../../components/Loader";
import PaddingContainer from "../../components/PaddingContainer";
import PageHeading from "../../components/PageHeading";
import PaymentStats from "../../components/PaymentStats";
import SearchInput from "../../components/SearchInput";
import StatusContainer from "../../components/statusContainer";
import TableValue from "../../components/TableValue";
import TabsContainer from "../../components/TabsContainer";
import { DownloadIcon, RefreshIcon, RemoveFilterIcon } from "../../constant";
import useDebounce from "../../customHooks/useDebounce";
import { downloadOptions, paymentMethods, statusOptions } from "../../data";
import { formatNumberWithCommas, formattedDate, formatText } from "../../helpers";
import { exportPaymentListingToPDF } from "../../pdfs/payments/paymentListing";
import { clearSelectDate } from "../../store/bookingFilterSlice";
import { convertUtcToLocalTime } from "../../helpers/exportHelpers";
import {
  clearFilters,
  setLastVisitedPage,
} from "../../store/bookingFilterSlice";
import { setPageLink, setPageTitle } from "../../store/pageSlice";
import useApi from "../../useApi";
import classes from "./Payments.module.css";
import Button from "../../components/Button";

// Add sorting fields enum
const PaymentSortFields = {
  DateTime: 1,
  Location: 3,
  Amount: 4,
  PaymentMode: 5,
  BookingReference: 6,
  PaymentReference: 7,
  PaymentType: 8,
  PlayerName: 9,
  // ReferenceId: 5,
  // Status: 6,
};

const getStatusLabel = (statusId) => {
  switch (statusId) {
    case 1:
      return "Received";
    case 2:
      return "Refunded";
    default:
      return "--";
  }
};

// PaymentReference
const initialValues = {
  locations: null,
  court: null,
  paymentMode: null,

  selectDate: [
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ],
};

export default function Payments() {
  const { allPreLoadedApis } = useSelector((state) => state);
  const bookingFilter = useSelector((state) => state.bookingFilter);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [first, setFirst] = useState(0);

  const [search, setSearch] = useState("");
  const debouncedValue = useDebounce(search, 600);
  const { getAllPayments, getPaymentStats, getLocations } = useApi();
  const [paymentStats, setPaymentStats] = useState({});
  const { values, setFieldValue, resetForm } = useFormik({
    initialValues,
  });
  const [rows, setRows] = useState(10);
  const [sortState, setSortState] = useState({
    field: null,
    order: null,
  });
  const [options, setOptions] = useState({
    status: statusOptions,
    paymentMode: paymentMethods,
    court: [
      ...[{ courtName: "All", courtId: 0 }],
      ...(allPreLoadedApis?.courts || []),
    ]?.map((data) => ({
      label: data?.courtName,
      value: data?.courtId,
    })),
    locations: [
      ...(allPreLoadedApis?.locations?.length > 1
        ? [{ locationName: "All", locationId: 0 }]
        : []),
      ...(allPreLoadedApis?.locations || []),
    ]?.map((data) => ({
      label: data?.locationName,
      value: data?.locationId,
    })),
  });

  const handleDownload = (type) => {
    if (!type) return;
    if (type === "csv") {
      // Use the existing CSV export function
      const csvContent = [
        // CSV headers
        [
          "Payment ID",
          "Booking ID",
          "Date",
          "Time",
          "Name",
          "Location",
          "Court",
          "Payment Mode",
          "Amount",
          "Status",
        ].join(","),

        // CSV rows
        ...payments.map((payment) =>
          [
            payment?.payNo?.split("-")?.slice(1).join("-") || "--",
            payment?.bookingNo?.split("-")?.slice(1).join("-") || "--",
            moment(convertUtcToLocalTime(payment?.paymentDate)).format(
              "DD-MMM-YY"
            ),
            moment(convertUtcToLocalTime(payment?.paymentDate)).format(
              "hh:mm A"
            ),
            payment?.userFullName || "--",
            payment?.locationName || "--",
            payment?.courtName || "--",
            paymentMethods?.find(
              (item) => item?.value === payment?.paymentModeId
            )?.label || "--",
            payment?.amount || 0,
            getStatusLabel(payment?.paymentStatusId),
          ].join(",")
        ),
      ].join("\n");

      // Create and download file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `payment_transactions_${moment().format(
        "YYYY-MM-DD"
      )}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (type === "pdf") {
      // Use the new PDF export function
      exportPaymentListingToPDF(payments);
    }

    // Reset the dropdown
    setFieldValue("download", null);
  };

  const columns = [
    {
      field: "payNo",
      header: "Payment ID",
      sortable: true,
      sortField: PaymentSortFields.PaymentReference,
      body: (rowData) => (
        <TableValue top={rowData?.payNo.split("-")?.slice(1).join("-")} />
      ),
    },
    {
      field: "bookingNo",
      header: "Reference ID",
      sortable: true,
      sortField: PaymentSortFields.BookingReference,
      body: (rowData) => (
        <TableValue top={rowData?.bookingNo?.split("-")?.slice(1).join("-")} />
      ),
    },
    {
      field: "userFullName",
      sortable: true,
      header: "Name",
      sortField: PaymentSortFields.PlayerName,
      body: (rowData) => (
        <TableValue
          top={rowData?.userFullName}
          down={`0${rowData?.userCellNumber?.slice(3)}`.replace(
            /(\d{4})(\d{7})/,
            "$1 $2"
          )}
        />
      ),
    },
    {
      field: "location",
      header: "Court",
      sortable: true,
      sortField: PaymentSortFields.Location,
      body: (rowData) => (
        <TableValue top={rowData?.courtName} down={rowData?.locationName} />
      ),
    },
    {
      field: "paymentDate",
      header: "Date",
      sortable: true,
      sortField: PaymentSortFields.DateTime,
      body: (rowData) => (
        <TableValue
          top={formattedDate(convertUtcToLocalTime(rowData?.paymentDate))}
          down={moment(convertUtcToLocalTime(rowData?.paymentDate)).format(
            "hh:mm A"
          )}
        />
      ),
    },
    {
      field: "paymentMode",
      header: "Payment Mode",
      sortable: true,
      sortField: PaymentSortFields.PaymentMode,
      body: (rowData) => (
        <TableValue
          top={
            paymentMethods.find((item) => item.value === rowData?.paymentModeId)
              ?.label || "--"
          }
        />
      ),
    },
    {
      field: "amount",
      header: "Amount",
      sortable: true,
      sortField: PaymentSortFields.Amount,
      body: (rowData) => (
        <TableValue top={formatNumberWithCommas(rowData?.amount)} />
      ),
    },
    {
      field: "isPaid",
      header: "Status",
      sortable: true,
      sortField: PaymentSortFields.Status,
      body: (rowData) => (
        <StatusContainer
          ball={<DotBall bookingStatus={rowData?.paymentStatusId} />}
          label={getStatusLabel(rowData?.paymentStatusId)}
        />
      ),
    },
    {
      field: "attachment",
      header: "Download",
      body: (rowData) => {
        return (
          <div
            disabled={!rowData?.attachment}
            className={clsx(classes.downloadIcon, {
              [classes.disabled]: !rowData?.attachment,
            })}
            onClick={() => {
              if (!rowData?.attachment) return;
              // Create anchor element to trigger download
              const link = document.createElement("a");
              link.href = rowData.attachment;
              link.download = `payment-${rowData?.payNo
                ?.split("-")
                ?.slice(1)
                .join("-")}`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          >
            <DownloadIcon />
          </div>
        );
      },
    },
  ];

  const handlePageChange = (e) => {
    setFirst(e.first);
    if (e.rows !== rows) {
      setRows(e.rows);
    }
    getPayments({
      ...getPaymentParams(),
      pageNumber: e.page + 1,
      pageSize: e.rows,
    });
  };

  const getPaymentParams = () => {
    let startDate = values?.selectDate?.[0]?.startDate;
    let endDate = values?.selectDate?.[0]?.endDate;

    return {
      search: debouncedValue,
      pageNumber: Math.floor(first / rows) + 1,
      pageSize: rows,
      paymentStartDate: startDate
        ? moment(startDate).format("YYYY-MM-DD")
        : null,
      paymentEndDate: endDate ? moment(endDate).format("YYYY-MM-DD") : null,
      locationId: values.locations || null,
      courtId: values.court || null,
      paymentModeId: values.paymentMode || null,
      isDesc: true,
      paymentAdminSortId: 0,
    };
  };

  const getPayments = async (params, allowLoading = true) => {
    if (allowLoading) setLoading(true);
    try {
      const result = await getAllPayments(params);
      if (result?.data?.headers?.error === 200) {
        setPayments(result?.data?.body?.items);
        setTotalCount(result?.data?.body?.totalItems);
      } else {
        toast.error(result?.data?.headers?.message);
      }
    } catch (error) {
      toast.error("Failed to fetch payments");
    }
    if (allowLoading) setLoading(false);
  };
  const getPaymentStatsApi = async (params, allowLoading) => {
    if (allowLoading) setLoading(true);
    try {
      const result = await getPaymentStats(params);
      if (result?.data?.headers?.error === 200) {
        setPaymentStats(result?.data?.body);
      } else {
        toast.error(result?.data?.headers?.message);
      }
    } catch (err) {
      toast.error(new Error(err)?.message);
    }
    if (allowLoading) setLoading(false);
  };
  useEffect(() => {
    // Clear booking filters when navigating to Payments
    if (bookingFilter.lastVisitedPage !== "payments") dispatch(clearFilters());
    // Mark that we're on the payments page
    dispatch(setLastVisitedPage("payments"));
    dispatch(setPageTitle("Payments"));
    dispatch(setPageLink(""));
  }, [dispatch, bookingFilter.lastVisitedPage]);

  useEffect(() => {
    if (debouncedValue) getPayments(getPaymentParams());
  }, [debouncedValue]);
  console.log("INSIDE");
  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.allSettled([
        // getPaymentsApi(getPaymentParams()),
        getPayments(getPaymentParams(), false),
        getPaymentStatsApi(
          {
            courtId: values.court || null,
            locationId: values.locations || null,
            paymentModeId: values.paymentMode || null,
            startDate: values.selectDate?.[0]?.startDate
              ? moment(values.selectDate?.[0]?.startDate).format("YYYY-MM-DD")
              : null,
            endDate: values.selectDate?.[0]?.endDate
              ? moment(values.selectDate?.[0]?.endDate).format("YYYY-MM-DD")
              : null,
          },
          false
        ),
      ]);
      setLoading(false);
    })();
  }, [values.locations, values.paymentMode, values.selectDate, values.court]);

  useEffect(() => {
    setOptions({
      ...options,
      court: values?.locations
        ? [
          ...[{ courtName: "All", courtId: 0 }],
          ...(allPreLoadedApis?.locations?.find(
            (data) => data?.locationId === values?.locations
          )?.courts || []),
        ]?.map((data) => ({
          label: data?.courtName,
          value: data?.courtId,
        }))
        : [
          ...[{ courtName: "All", courtId: 0 }],
          ...(allPreLoadedApis?.courts || []),
        ]?.map((data) => ({
          label: data?.courtName,
          value: data?.courtId,
        })),
    });
  }, [values?.locations]);

  const handleSort = (event) => {
    console.log("Payment handleSort event:", event);

    setSortState({
      field: event.sortField,
      order: event.sortOrder,
    });

    getPayments({
      ...getPaymentParams(),
      isDesc: event.sortOrder === -1,
      paymentAdminSortId: event.sortField || 0,
    });
  };

  // Add reset filters function
  const resetAllFilters = () => {
    // Reset form values
    resetForm();

    // Reset date picker explicitly
    setFieldValue("selectDate", [
      {
        startDate: null,
        endDate: null,
        key: "selection",
      },
    ]);
    dispatch(clearSelectDate());

    // Reset search
    setSearch("");
    dispatch(clearFilters());

    // Reset sort
    setSortState({
      field: PaymentSortFields.PaymentReference,
      order: -1,
    });

    // Reset pagination
    setFirst(0);

    // Get payments with reset filters
    getPayments({
      ...getPaymentParams(),
      pageNumber: 1,
      pageSize: rows,
      paymentAdminSortId: PaymentSortFields.PaymentReference,
      isDesc: true,
    });
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <PaddingContainer>
          <div className={classes.titleContainer}>
            <div className={classes.titleContainer}>
              <h2>Earnings Overview</h2>
              <Button
                leftIcon={<RefreshIcon />}
                className={classes.refreshButton}
                onClick={() => {
                  // Get payment data
                  (async () => {
                    setLoading(true);
                    await Promise.allSettled([
                      // getPaymentsApi(getPaymentParams()),
                      getPayments(getPaymentParams(), false),
                      getPaymentStatsApi(
                        {
                          courtId: values.court || null,
                          locationId: values.locations || null,
                          paymentModeId: values.paymentMode || null,
                          startDate: values.selectDate?.[0]?.startDate
                            ? moment(values.selectDate?.[0]?.startDate).format(
                              "YYYY-MM-DD"
                            )
                            : null,
                          endDate: values.selectDate?.[0]?.endDate
                            ? moment(values.selectDate?.[0]?.endDate).format(
                              "YYYY-MM-DD"
                            )
                            : null,
                        },
                        false
                      ),
                    ]);
                    setLoading(false);
                  })();
                }}
              />
            
            </div>
            <div className={classes.tabsWrapper}>
              <TabsContainer
                tabs={
                  <>
                    {Object.keys(initialValues)
                      .slice(0, -1) // Skip 'download' to handle it separately
                      ?.map((filter) => {
                        if (
                          filter === "locations" &&
                          options?.locations?.length <= 1
                        ) {
                          return null;
                        }
                        return (
                          <Dropdown
                            key={filter}
                            placeholder={formatText(filter)}
                            className={clsx(
                              classes.filterAction,
                              classes.bgWhite
                            )}
                            value={values[filter]}
                            onChange={(e) => {
                              setFieldValue(filter, e);
                            }}
                            options={options?.[filter] || []}
                          />
                        );
                      })}
                      <div className={classes.combineFilter}>
                    <div className={classes.datePickerContainer}>
                    <DatePicker
                        type="range"
                        value={values?.selectDate}
                        onChange={(e) => setFieldValue("selectDate", e)}
                      /> 
                      
                      {values?.selectDate?.[0]?.startDate && (
                        <button
                          type="button"
                          className={classes.resetDateButton}
                          onClick={() => {
                            setFieldValue("selectDate", [
                              {
                                startDate: null,
                                endDate: null,
                                key: "selection",
                              },
                            ]);
                            dispatch(clearSelectDate());
                          }}
                        >
                          <RxCross2 className={classes.resetIcon} />
                        </button>
                      )}
                    </div>
                    <Button
                leftIcon={<RemoveFilterIcon />}
                className={classes.refreshButton}
                onClick={resetAllFilters}
              />
                    </div>
                    <Dropdown
                className={clsx(classes.filterAction, classes.bgWhite)}
                placeholder={"Export"}
                icon={<DownloadIcon />}
                options={downloadOptions}
                value={values.download}
                onChange={(e) => {
                  setFieldValue("download", e);
                  handleDownload(e);
                }}
              />
                  </>
                }
              />

              {/* Add an explicit export dropdown */}
             
            </div>
          </div>
          <PaymentStats className={classes.mt24} data={paymentStats} period={values?.selectDate?.[0]} />
          <ol className={classes.titleStrip}>
            <li>
              <PageHeading label="Transaction History" />
            </li>
            <li>
              <div className={classes.filterActions}>
                <div className={classes.searchContainer}>
                  <SearchInput
                    placeholder={"Search By Reference ID/Name"}
                    onChange={setSearch}
                    value={search}
                  />
                </div>
              </div>
            </li>
          </ol>

          <DataTableWrapper
            search={search}
            data={payments}
            columns={columns}
            loading={loading}
            totalRecords={totalCount}
            onPageChange={handlePageChange}
            first={first}
            rows={rows}
            onSort={handleSort}
            sortField={sortState.field}
            sortOrder={sortState.order}
          />
        </PaddingContainer>
      )}
    </>
  );
}
